const toNumber = (value) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? 0 : n;
};

export function computeTotal(rows, metric) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    total += toNumber(rows[i]?.[metric]);
  }
  return total;
}

export function computeAverage(rows, metric) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  return Math.floor(computeTotal(rows, metric) / rows.length);
}

export function computeStandardDeviation(rows, metric) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  const average = computeTotal(rows, metric) / rows.length;
  let squared = 0;
  for (let i = 0; i < rows.length; i++) {
    squared += Math.pow(toNumber(rows[i]?.[metric]) - average, 2);
  }
  return Math.floor(Math.sqrt(squared / rows.length) * 100) / 100;
}

export function computeErrorPercentage(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  let errors = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i]?.error) errors += 1;
  }
  const errorPercentage = (errors / rows.length) * 100;
  return errorPercentage ? Math.floor(errorPercentage) : 0;
}

export function computeCorrectPercentage(rows) {
  const error = computeErrorPercentage(rows);
  return error ? Math.floor(100 - error) : 100;
}

export function computeSessionCompleteness(rows) {
  if (!Array.isArray(rows)) return 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (
      toNumber(row.visuMotor) === 0 ||
      toNumber(row.motor) === 0 ||
      toNumber(row.cognitiveMotor) === 0
    ) {
      return 0;
    }
  }
  return 1;
}

export function computeMetrics(
  rows,
  { isCurrentSession = true, storedAnalytics = null, useStored = false } = {}
) {
  const hasStored =
    !isCurrentSession &&
    storedAnalytics &&
    storedAnalytics.motorTotal !== 0 &&
    storedAnalytics.responseTotal !== 0 &&
    storedAnalytics.visuMotorTotal !== 0 &&
    storedAnalytics.motorMean !== 0 &&
    storedAnalytics.responseMean !== 0 &&
    storedAnalytics.visuMotorMean !== 0 &&
    storedAnalytics.motorSd !== 0 &&
    storedAnalytics.responseSd !== 0 &&
    storedAnalytics.visuMotorSd !== 0;

  if (hasStored && useStored) {
    return {
      totalVisuMotor: storedAnalytics.visuMotorTotal,
      averageVisuMotor: storedAnalytics.visuMotorMean,
      standardDeviationVisuMotor: storedAnalytics.visuMotorSd,
      totalMotor: storedAnalytics.motorTotal,
      averageMotor: storedAnalytics.motorMean,
      standardDeviationMotor: storedAnalytics.motorSd,
      totalCognitiveMotor: storedAnalytics.responseTotal,
      averageCognitiveMotor: storedAnalytics.responseMean,
      standardDeviationCognitiveMotor: storedAnalytics.responseSd,
      correctPercentage: computeCorrectPercentage(rows),
      errorPercentage: computeErrorPercentage(rows),
    };
  }

  return {
    totalVisuMotor: computeTotal(rows, "visuMotor"),
    averageVisuMotor: computeAverage(rows, "visuMotor"),
    standardDeviationVisuMotor: computeStandardDeviation(rows, "visuMotor"),
    totalMotor: computeTotal(rows, "motor"),
    averageMotor: computeAverage(rows, "motor"),
    standardDeviationMotor: computeStandardDeviation(rows, "motor"),
    totalCognitiveMotor: computeTotal(rows, "cognitiveMotor"),
    averageCognitiveMotor: computeAverage(rows, "cognitiveMotor"),
    standardDeviationCognitiveMotor: computeStandardDeviation(
      rows,
      "cognitiveMotor"
    ),
    correctPercentage: computeCorrectPercentage(rows),
    errorPercentage: computeErrorPercentage(rows),
  };
}